import { createFileRoute } from "@tanstack/react-router";
import { AppNav } from "@/components/AppNav";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [{ title: "Política de Privacidade — Busca Blindados" }],
  }),
  component: PrivacidadePage,
});

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-lg text-foreground sm:text-xl">
        {n}. {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/80">{children}</div>
    </section>
  );
}

function PrivacidadePage() {
  return (
    <div className="min-h-screen text-foreground">
      <AppNav />
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-6">
        <header>
          <h1 className="font-display text-3xl text-foreground sm:text-4xl">
            Política de Privacidade
          </h1>
          <p className="mt-2 text-xs text-muted-foreground">
            Versão 1.1 — vigência a partir de 21 de outubro de 2026
          </p>
          <p className="mt-4 text-sm text-foreground/80 leading-relaxed">
            Dúvidas ou solicitações sobre seus dados? Entre em contato através da nossa{" "}
            <a href="/contato" className="text-foreground underline underline-offset-2 hover:text-primary">
              página de contato
            </a>
            .
          </p>
        </header>

        <Section n="1" title="Quem somos e papel na LGPD">
          <p>
            A Busca Blindados é uma plataforma de agregação e indexação de anúncios públicos de
            veículos publicados por revendas e terceiros em seus próprios sites, oferecendo a
            usuários cadastrados busca avançada, alertas e funcionalidades premium mediante
            assinatura ("Serviço"). A Busca Blindados é a controladora dos dados pessoais
            tratados no âmbito do Serviço.
          </p>
        </Section>

        <Section n="2" title="Dados que tratamos e finalidades">
          <p className="font-medium text-foreground">(a) Dados de usuários do Serviço (cadastro e assinatura):</p>
          <ul className="ml-4 list-disc space-y-1.5">
            <li>
              <span className="font-medium text-foreground/90">Identificação:</span> nome, CPF e
              e-mail.
            </li>
            <li>
              <span className="font-medium text-foreground/90">Dados de acesso:</span> senha
              (criptografada), histórico de logins, IP, registros de aplicação e logs.
            </li>
            <li>
              <span className="font-medium text-foreground/90">Preferências:</span> filtros
              salvos, alertas configurados, histórico de interação com a plataforma.
            </li>
            <li>
              <span className="font-medium text-foreground/90">Dados financeiros:</span> nos
              planos de assinatura, dados de cobrança são processados pelo gateway terceirizado
              Stripe (PCI-DSS). Não armazenamos dados de cartão de crédito.
            </li>
          </ul>

          <p className="font-medium text-foreground">(b) Dados constantes de anúncios públicos de terceiros:</p>
          <p>
            O Serviço indexa dados objetivos publicados pelas revendas em suas páginas abertas
            (marca, modelo, ano, quilometragem, preço, cidade, descrição sumária e URL do anúncio
            original). Mascaramos, se houver, placa, chassi e RENAVAM. Não exibimos fotos ou
            textos autorais sem autorização. Eventuais dados pessoais incidentais são tratados
            com base no legítimo interesse.
          </p>

          <p className="font-medium text-foreground">(c) Cookies e tecnologias similares:</p>
          <p>
            Utilizamos cookies essenciais para o funcionamento da plataforma, bem como cookies
            analíticos e de marketing.
          </p>
          <p className="font-medium text-foreground">(d) Proteção contra bots — Cloudflare Turnstile:</p>
          <p>
            Para proteger o Serviço contra uso automatizado abusivo, utilizamos o Cloudflare
            Turnstile, um serviço de verificação de humanos operado pela Cloudflare, Inc. O
            Turnstile pode coletar dados técnicos do dispositivo e da conexão para fins de
            detecção de bots, conforme descrito na{" "}
            <a
              href="https://www.cloudflare.com/en-gb/turnstile-privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-2 hover:text-primary"
            >
              Política de Privacidade do Cloudflare Turnstile
            </a>
            .
          </p>
          <p className="font-medium text-foreground">(e) Comunicações por e-mail sobre o Serviço:</p>
          <p>
            Além dos e-mails transacionais e dos alertas que você configura, usamos o e-mail de
            usuários cadastrados com e-mail confirmado para enviar novidades do Serviço, como
            novas funcionalidades e avisos relevantes, com base no legítimo interesse (art. 7º,
            IX, da LGPD). Todo envio traz um link para cancelar o recebimento dessas
            comunicações, e você também pode pedir a interrupção pela nossa{" "}
            <a href="/contato" className="text-foreground underline underline-offset-2 hover:text-primary">
              página de contato
            </a>
            . Para isso, o endereço de e-mail é mantido em nosso provedor de envio de e-mails.
          </p>
          <p className="font-medium text-foreground">(f) Inscrição na Tabela de referência de preço:</p>
          <p>
            Ao pedir a Tabela de referência de preço em nosso site, sem criar conta, você nos
            informa seu e-mail e consente em receber a edição mensal da tabela (art. 7º, I, da
            LGPD). Registramos o e-mail, a data e o horário do pedido, o endereço IP e a página
            de onde ele partiu, para comprovar o consentimento e prevenir abuso. Esses dados
            ficam enquanto durar a inscrição e pelo prazo necessário para comprovar o
            consentimento. Quem se inscreve apenas na Tabela não recebe as novidades descritas
            no item (e). Você cancela a qualquer momento pelo link presente em todo e-mail ou
            pela nossa{" "}
            <a href="/contato" className="text-foreground underline underline-offset-2 hover:text-primary">
              página de contato
            </a>
            .
          </p>
        </Section>

        <Section n="3" title="Compartilhamento">
          <p>
            Compartilhamos dados com provedores de infraestrutura (hospedagem, gateway de
            pagamento, envio de e-mails) sob contrato com cláusulas de proteção de dados.
            Também com autoridades por obrigação legal ou ordem judicial. Não vendemos dados
            pessoais. Transferência internacional realizada apenas para países com nível adequado
            de proteção ou mediante cláusulas-padrão.
          </p>
        </Section>

        <Section n="4" title="Titulares de Dados Contidos em Anúncios">
          <p>
            Caso V.Sa. seja titular de dados pessoais que tenham aparecido em anúncio público
            indexado, pode solicitar exclusão da nossa base através da nossa{" "}
            <a href="/contato" className="text-foreground underline underline-offset-2 hover:text-primary">
              página de contato
            </a>
            . Atenderemos no prazo legal, sem prejuízo de o anúncio permanecer no site original
            do anunciante.
          </p>
        </Section>

        <Section n="5" title="Retenção">
          <ul className="ml-4 list-disc space-y-1.5">
            <li>
              <span className="font-medium text-foreground/90">Dados de usuário:</span> enquanto
              durar a conta + 5 anos para prescrição cível.
            </li>
            <li>
              <span className="font-medium text-foreground/90">Logs de acesso:</span> 6 meses.
            </li>
            <li>
              <span className="font-medium text-foreground/90">Dados de anúncios:</span> enquanto
              o anúncio estiver ativo no site de origem ou houver legítimo interesse.
            </li>
          </ul>
        </Section>

        <Section n="6" title="Segurança">
          <p>
            Adotamos medidas técnicas e organizacionais adequadas: criptografia em trânsito e em
            repouso, controle de acesso por princípio do menor privilégio, monitoramento contínuo
            e plano de resposta a incidentes.
          </p>
        </Section>

        <Section n="7" title="Alterações">
          <p>
            Esta Política pode ser atualizada a qualquer momento. A versão vigente e a data de
            vigência ficam sempre publicadas nesta página. O uso continuado do Serviço após a
            vigência da nova versão implica aceitação.
          </p>
        </Section>
      </main>
    </div>
  );
}
